import { Image as ImagemIcone, Loader2, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { RecorteDeImagemDialogo } from '@/shared/components/RecorteDeImagemDialogo';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { cn } from '@/shared/lib/cn';

/**
 * Prévia + botão de envio de uma imagem já persistida no servidor.
 *
 * Promovido de dentro de `ImagensDoParceiroDialogo.tsx`, onde era privado —
 * o mesmo caminho que o `Campo` fez a partir da `RegistroPage`.
 *
 * ⚠️ A VALIDAÇÃO É DO SERVIDOR, sempre. O `accept` e o texto de ajuda são
 * orientação para quem escolhe o arquivo; nenhum dos dois impede um POST
 * direto, e a mensagem de recusa já vem pronta de lá citando a regra ("A
 * imagem tem 800×400 (2.00:1). Esta posição exige 3:1 — por exemplo
 * 1200×400."). Reescrever esse texto aqui criaria dois lugares para divergir.
 *
 * Com `recorte`, porém, essa recusa deixa de acontecer na prática: o arquivo
 * escolhido passa pelo `RecorteDeImagemDialogo` e chega ao servidor já na
 * proporção exigida. A regra continua no servidor; o que muda é que ninguém
 * mais precisa recortar em outro programa para conseguir enviar.
 *
 * Sem prévia otimista (`URL.createObjectURL`) de propósito: o que a tela mostra
 * é o que o servidor aceitou. Uma prévia local exibiria a imagem recusada como
 * se tivesse entrado.
 */
export function CampoDeImagem({
  rotulo,
  ajuda,
  urlAtual,
  proporcao,
  redonda,
  enviando,
  aoEnviar,
  aoRemover,
  pendente,
  recorte,
}: {
  rotulo: string;
  ajuda: string;
  urlAtual: string | null;
  /** Classe de aspect-ratio do quadro da prévia — `aspect-[3/1]`, `aspect-square`. */
  proporcao: string;
  /** Avatar. Recorta em círculo e faz a imagem cobrir o quadro. */
  redonda?: boolean;
  enviando: boolean;
  aoEnviar: (arquivo: File) => void;
  /** Quando ausente, o campo só troca — não oferece remover. */
  aoRemover?: () => void;
  /**
   * Imagem escolhida que **ainda não foi ao servidor** — a única exceção à
   * regra de "sem prévia otimista" descrita acima, e ela é estrutural: no
   * cadastro de um registro novo não existe id para receber o upload, então a
   * escolha precisa esperar o salvamento.
   *
   * Vem com aviso na tela de propósito. Sem ele, a prévia local prometeria uma
   * imagem que o servidor ainda pode recusar — que é justamente o que a regra
   * existe para evitar.
   */
  pendente?: { url: string; nome: string } | null;
  /**
   * Presente quando a posição exige uma proporção exata: escolher um arquivo
   * abre o enquadramento em vez de mandar a imagem crua. Os dois números têm
   * que bater com o perfil do servidor (`infra/storage/perfis.ts`) —
   * `proporcao` com a `proporcao` de lá, `larguraSaida` com um valor >= a
   * `larguraMinima`, senão o enquadramento só troca uma recusa por outra.
   *
   * `encaixar` abre com a imagem inteira dentro do quadro em vez de
   * preenchendo — ver `RecorteDeImagemDialogo`.
   */
  recorte?: { proporcao: number; larguraSaida: number; encaixar?: boolean };
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [paraRecortar, setParaRecortar] = useState<File | null>(null);
  const mostrada = pendente?.url ?? urlAtual;

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{rotulo}</Label>

      {/* Fundo branco: logo costuma vir com fundo transparente. */}
      <div
        className={cn(
          proporcao,
          'w-full border border-border bg-white flex items-center justify-center overflow-hidden',
          redonda ? 'rounded-full max-w-[160px]' : 'rounded-lg',
        )}
      >
        {mostrada ? (
          // Sem padding: o quadro já tem a proporção exata que o servidor exige,
          // então um logo 3:1 preenche de borda a borda. Qualquer padding tira
          // altura, o `object-contain` passa a limitar pela altura e a imagem
          // encolhe deixando faixas brancas nas laterais.
          <img
            src={mostrada}
            alt={rotulo}
            className={cn('w-full h-full', redonda ? 'object-cover' : 'object-contain')}
          />
        ) : (
          <ImagemIcone className="w-8 h-8 text-muted-foreground/40" />
        )}
      </div>

      <input
        ref={entrada}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        aria-label={`Arquivo de ${rotulo.toLocaleLowerCase('pt-BR')}`}
        onChange={(e) => {
          const arquivo = e.target.files?.[0];
          if (arquivo) {
            if (recorte) setParaRecortar(arquivo);
            else aoEnviar(arquivo);
          }
          // Zerar permite reenviar o MESMO arquivo depois de uma recusa — sem
          // isto o `change` não dispara na segunda tentativa.
          e.target.value = '';
        }}
      />

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={enviando}
          onClick={() => entrada.current?.click()}
        >
          {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {mostrada ? 'Trocar' : 'Enviar'}
        </Button>

        {aoRemover && mostrada && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2 text-destructive hover:text-destructive"
            disabled={enviando}
            onClick={aoRemover}
          >
            <Trash2 className="w-4 h-4" />
            Remover
          </Button>
        )}
      </div>

      {pendente ? (
        <p className="text-xs text-amber-600">
          {`“${pendente.nome}” será enviada ao salvar — o servidor ainda vai validar.`}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">{ajuda}</p>
      )}

      {recorte && paraRecortar && (
        <RecorteDeImagemDialogo
          aberto
          aoFechar={() => setParaRecortar(null)}
          arquivo={paraRecortar}
          proporcao={recorte.proporcao}
          larguraSaida={recorte.larguraSaida}
          encaixar={recorte.encaixar}
          redonda={redonda}
          enviando={enviando}
          aoConfirmar={(recortado) => {
            setParaRecortar(null);
            aoEnviar(recortado);
          }}
        />
      )}
    </div>
  );
}
